import { signal } from '@preact/signals'
import { storedToken } from './client'
import { apiBase, setRegion, type Region } from './region'
import type { ConsentInput } from './consent'
import { lang } from '../i18n'

/**
 * Вход по почте. Пароля нет: на адрес уходит шестизначный код и ссылка подтверждения.
 *
 * Код можно ввести руками, а можно открыть ссылку на том устройстве, где лежит почта —
 * тогда ожидающее устройство войдёт само. Ссылка при этом не тупик: она возвращает человека
 * в приложение и входит в том браузере, где её открыли.
 */

const SESSION_KEY = 'moonwake.session'
const EMAIL_KEY = 'moonwake.email'

/** Почта вошедшего человека; null — вход не выполнен. */
export const account = signal<string | null>(readLocal(EMAIL_KEY))

/**
 * Только что вошли. Сообщать об этом надо один раз, а не при каждом открытии приложения:
 * человек и так знает, под кем он сидит.
 */
export const justSignedIn = signal(false)

export function sessionToken(): string | null {
  return readLocal(SESSION_KEY)
}

function readLocal(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null
  } catch {
    return null
  }
}

function writeLocal(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(key, value)
  } catch {
    // приватный режим — переживём, вход не сохранится до перезагрузки
  }
}

export function rememberSession(token: string, email: string | null): void {
  if (typeof token !== 'string' || token === '') return
  writeLocal(SESSION_KEY, token)
  if (email !== null) writeLocal(EMAIL_KEY, email)
  account.value = email
  justSignedIn.value = true
}

/** Почта аккаунта изменилась, ключ сессии прежний. */
export function rememberEmail(email: string): void {
  try {
    globalThis.localStorage?.setItem(EMAIL_KEY, email)
  } catch {
    // приватный режим
  }
  account.value = email
}

export function signOut(): void {
  try {
    globalThis.localStorage?.removeItem(SESSION_KEY)
    globalThis.localStorage?.removeItem(EMAIL_KEY)
  } catch {
    // ничего
  }
  account.value = null
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const device = storedToken()
  const response = await fetch(`${apiBase()}${path}`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      // Устройство нужно, чтобы привязать его к аккаунту: данные с телефона не должны потеряться.
      ...(device === null ? {} : { authorization: `Device ${device}` }),
    },
    body: JSON.stringify(body),
  })
  const json = (await response.json().catch(() => ({}))) as T & { error?: string }
  if (!response.ok) throw new AuthError(json.error ?? String(response.status), response.status)
  return json
}

export class AuthError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
  ) {
    super(code)
    this.name = 'AuthError'
  }
}

export interface PendingLogin {
  requestId: string
  waitSecret: string
  expiresAt: number
}

/** Просит сервер отправить письмо с кодом — на языке приложения (решение заказчика 14). */
/**
 * Запросить код. Вход — только в существующий аккаунт; заводит аккаунт анкета, и она
 * передаёт `signup`. Без признака незнакомая почта получает отказ `no_account`: так
 * человек, ошибившийся страной, не заводит молча второй аккаунт в другой базе.
 */
export async function requestCode(
  email: string,
  { signup = false, consents, region }: { signup?: boolean; consents?: ConsentInput; region?: string } = {},
): Promise<PendingLogin> {
  return post<PendingLogin>('/auth/request', {
    email,
    lang: lang.value,
    ...(signup ? { signup: true } : {}),
    ...(consents ? { consents, channel: 'web' } : {}),
    ...(region ? { region } : {}),
  })
}

/** Проверяет введённый код. Возвращает ключ сессии. */
export async function verifyCode(requestId: string, code: string): Promise<string> {
  const { token } = await post<{ token: string }>('/auth/verify', { requestId, code })
  return token
}

/**
 * Чем кончилось ожидание ссылки из письма.
 *
 * Раньше на всё отвечали одним `null`, и экран не мог отличить «ещё не подтвердили»
 * от «такого запроса нет» — поэтому спрашивал вечно, раз в две секунды, даже когда
 * сервер твёрдо отвечал 404 (найдено в журнале 21.09.2026: 6270 таких запросов за сутки).
 */
export type LoginWait =
  /** ждём дальше */
  | { state: 'waiting' }
  /** такого запроса на сервере нет: спрашивать больше не о чем */
  | { state: 'gone' }
  /** вход подтверждён; ключа может не быть, если его уже забрало другое устройство */
  | { state: 'signed'; token: string | null }

/** Спрашивает, не подтвердили ли вход по ссылке из письма. */
export async function checkConfirmed(pending: PendingLogin): Promise<LoginWait> {
  const query = `request=${encodeURIComponent(pending.requestId)}&secret=${encodeURIComponent(pending.waitSecret)}`
  let response: Response
  try {
    response = await fetch(`${apiBase()}/auth/status?${query}`)
  } catch {
    return { state: 'waiting' } // нет сети — подождём следующего захода
  }
  if (response.status === 404) return { state: 'gone' }
  if (!response.ok) return { state: 'waiting' }
  const { confirmed, token } = (await response.json()) as { confirmed: boolean; token: string | null }
  return confirmed ? { state: 'signed', token } : { state: 'waiting' }
}

/**
 * Адрес вида `/app#login=...` — человек пришёл по ссылке из письма.
 * Ключ забирается из якоря и сразу стирается, чтобы не остался в истории браузера.
 */
export async function claimFromUrl(): Promise<boolean> {
  const hash = globalThis.location?.hash ?? ''
  const match = /^#login=([^&]+)(?:&region=(ru|world))?$/.exec(hash)
  if (match === null) return false

  const linkToken = decodeURIComponent(match[1]!)
  history.replaceState(null, '', globalThis.location.pathname + globalThis.location.search)
  // Ссылку выдал сервер своего региона — забирать вход надо у него же, иначе ключ
  // неизвестен. Регион едет в якоре вместе с ключом и становится выбором устройства
  const выдал = match[2] as Region | undefined
  if (выдал !== undefined) await setRegion(выдал)
  try {
    const { token, email } = await post<{ token: string; email?: string }>('/auth/claim', { linkToken })
    rememberSession(token, email ?? null)
    return true
  } catch {
    return false
  }
}
